import {
  useState, useMemo 
} from 'react';
import {
  BarChart3,
  Search,
  X,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  useReviews,
  ReviewCard,
  ReviewDetailModal,
} from '@/features/performance';
import type {
  ReviewStatus, ReviewPeriod, Review 
} from '@/features/performance';
import { toast } from 'sonner';

const REVIEW_STATUSES: {
  value: ReviewStatus;
  label: string 
}[] = [
  {
    value: 'draft',
    label: 'Draft' 
  },
  {
    value: 'in-progress',
    label: 'In Progress' 
  },
  {
    value: 'pending-approval',
    label: 'Pending Approval' 
  },
  {
    value: 'completed',
    label: 'Completed' 
  },
];

const REVIEW_PERIODS: {
  value: ReviewPeriod;
  label: string 
}[] = [
  {
    value: 'quarterly',
    label: 'Quarterly' 
  },
  {
    value: 'semi-annual',
    label: 'Semi-Annual' 
  },
  {
    value: 'annual',
    label: 'Annual' 
  },
];

export function ReviewsView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReviewStatus | ''>('');
  const [periodFilter, setPeriodFilter] = useState<ReviewPeriod | ''>('');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  const {
    data, isLoading, error, refetch 
  } = useReviews({
    filters: {
      search: searchQuery || undefined,
      status: statusFilter || undefined,
      period: periodFilter || undefined,
    },
  });

  const reviewsList = data?.data ?? [];

  const stats = useMemo(() => {
    const draft = reviewsList.filter((r) => r.status === 'draft').length;
    const inProgress = reviewsList.filter((r) => r.status === 'in-progress').length;
    const pendingApproval = reviewsList.filter((r) => r.status === 'pending-approval').length;
    const completed = reviewsList.filter((r) => r.status === 'completed').length;
    return {
      draft,
      inProgress,
      pendingApproval,
      completed,
      total: reviewsList.length,
    };
  }, [reviewsList]);

  const handleAddReview = () => {
    toast.info('Create review functionality coming soon');
  };

  const handleEditReview = (review: Review) => {
    toast.info(`Edit review for ${review.employeeName}`);
    setSelectedReview(null);
  };

  const handleDeleteReview = (review: Review) => {
    toast.success(`Deleted review for ${review.employeeName}`);
    setSelectedReview(null);
    refetch();
  };

  const handleSubmitForApproval = (review: Review) => {
    toast.success(`Review for ${review.employeeName} submitted for approval`);
    setSelectedReview(null);
    refetch();
  };

  const handleApprove = (review: Review) => {
    toast.success(`Review for ${review.employeeName} approved`);
    setSelectedReview(null);
    refetch();
  };

  const handleCardClick = (review: Review) => {
    setSelectedReview(review);
  };

  const exportData = reviewsList.map((r) => ({
    employee: r.employeeName,
    role: r.employeeRole,
    reviewer: r.reviewerName,
    period: r.period,
    periodStart: r.periodStart,
    periodEnd: r.periodEnd,
    status: r.status,
    rating: r.overallRating ?? '',
    dueDate: r.dueDate,
    completedAt: r.completedAt ?? '',
    site: r.siteName,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Review"
        entityNamePlural="Reviews"
        selectedCount={0}
        onAdd={handleAddReview}
        exportData={exportData}
        exportFilename="performance-reviews.csv"
      />

      <ReviewDetailModal
        review={selectedReview}
        isOpen={!!selectedReview}
        onClose={() => setSelectedReview(null)}
        onEdit={handleEditReview}
        onDelete={handleDeleteReview}
        onSubmitForApproval={handleSubmitForApproval}
        onApprove={handleApprove}
      />

      <StatsCards stats={stats} />

      <SearchAndFilter
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        periodFilter={periodFilter}
        setPeriodFilter={setPeriodFilter}
      />

      <ReviewsGrid
        reviewsList={reviewsList}
        isLoading={isLoading}
        error={error}
        onCardClick={handleCardClick}
        onRefetch={refetch}
      />
    </>
  );
}

interface StatsCardsProps {
  readonly stats: {
    total: number;
    draft: number;
    inProgress: number;
    pendingApproval: number;
    completed: number;
  };
}

function StatsCards({ stats }: StatsCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Reviews</CardTitle>
          <BarChart3 className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
          <Clock className="h-5 w-5 text-blue-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Pending Approval</CardTitle>
          <AlertCircle className="h-5 w-5 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-amber-600">{stats.pendingApproval}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
          <CheckCircle className="h-5 w-5 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
        </CardContent>
      </Card>
    </div>
  );
}

interface SearchAndFilterProps {
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly statusFilter: ReviewStatus | '';
  readonly setStatusFilter: (status: ReviewStatus | '') => void;
  readonly periodFilter: ReviewPeriod | '';
  readonly setPeriodFilter: (period: ReviewPeriod | '') => void;
}

function SearchAndFilter({
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  periodFilter,
  setPeriodFilter,
}: SearchAndFilterProps) {
  return (
    <div className="flex gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search reviews..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value as ReviewStatus | '')}
        className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">All Statuses</option>
        {REVIEW_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      <select
        value={periodFilter}
        onChange={(e) => setPeriodFilter(e.target.value as ReviewPeriod | '')}
        className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">All Periods</option>
        {REVIEW_PERIODS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface ReviewsGridProps {
  readonly reviewsList: Review[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly onCardClick: (review: Review) => void;
  readonly onRefetch: () => void;
}

function ReviewsGrid({
  reviewsList,
  isLoading,
  error,
  onCardClick,
  onRefetch,
}: ReviewsGridProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <BarChart3 className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load reviews</p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (reviewsList.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No reviews found</p>
          <p className="text-muted-foreground">Create a review to get started</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {reviewsList.map((review) => (
        <ReviewCard
          key={review.reviewId}
          review={review}
          onClick={() => onCardClick(review)}
        />
      ))}
    </div>
  );
}
